// References (`[[kind:id|name]]` and title links `[[name]]`) as an inline atom
// node, `trameriaLink`.
//
// The syntax is not defined here: reading uses `matchRefAtStart` and writing
// `formatRef` / `formatTitleRef` from `@trameria/core/refs-shared`, the one
// definition the server and the screen also read. Any snake_case kind is a
// reference; whether this editor (or anything) knows the kind does not matter -
// a kind it does not know is kept and written back as it was.
//
// Code is never turned into a reference: this is an inline tokenizer, and a code
// span is consumed by marked's codespan tokenizer from its opening backtick, so
// text inside it never reaches here; fences are taken whole at the block level.
import { Node } from "@tiptap/core";
import {
  formatRef,
  formatTitleRef,
  matchRefAtStart,
} from "@trameria/core/refs-shared";

export interface TrameriaLinkAttrs {
  /** null for a title link. */
  kind: string | null;
  refId: string | null;
  label: string;
}

/** The markdown of a reference node: `[[kind:id|name]]`, or `[[name]]` without a kind and id. */
export function trameriaLinkToRaw(attrs: {
  kind?: unknown;
  refId?: unknown;
  label?: unknown;
}): string {
  const label = typeof attrs.label === "string" ? attrs.label : "";
  if (
    typeof attrs.kind === "string" &&
    attrs.kind !== "" &&
    typeof attrs.refId === "string" &&
    attrs.refId !== ""
  ) {
    return formatRef({ kind: attrs.kind, id: attrs.refId, label });
  }
  return formatTitleRef(label);
}

/** The first index where a reference starts in `src`, or -1. */
function refSearchIndex(src: string): number {
  for (let at = src.indexOf("[["); at !== -1; at = src.indexOf("[[", at + 1)) {
    if (matchRefAtStart(src.slice(at))) return at;
  }
  return -1;
}

export const TrameriaLink = Node.create({
  name: "trameriaLink",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      kind: { default: null as string | null },
      refId: { default: null as string | null },
      label: { default: "" },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-trameria-link]" }];
  },

  renderHTML({ node }) {
    const attrs = node.attrs as TrameriaLinkAttrs;
    return [
      "span",
      {
        "data-trameria-link": "true",
        "data-trameria-link-kind": attrs.kind ?? "",
        "data-trameria-link-ref-id": attrs.refId ?? "",
      },
      attrs.label,
    ];
  },

  markdownTokenName: "trameriaLink",
  markdownTokenizer: {
    name: "trameriaLink",
    level: "inline",
    start(src: string) {
      return refSearchIndex(src);
    },
    tokenize(src: string) {
      const ref = matchRefAtStart(src);
      if (!ref) return undefined;
      return {
        type: "trameriaLink",
        raw: ref.raw,
        kind: ref.kind,
        refId: ref.id,
        label: ref.label,
      };
    },
  },
  parseMarkdown(token, h) {
    return h.createNode(
      "trameriaLink",
      { kind: token.kind, refId: token.refId, label: token.label },
      [],
    );
  },
  renderMarkdown(node) {
    return trameriaLinkToRaw(node.attrs as TrameriaLinkAttrs);
  },
});
